import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../Lib/Firebase";
import { getStudentCourses, listOpenCourseRuns, type OpenCourseRun, type StudentCourse } from "../Lib/courseApi";
import {
    buildCareerJourney, completedStudentCourses, currentStudentCourses, selectRecommendedProgram,
    upcomingStudentCourses, type InterviewActivity, type InterviewFeedback,
} from "../data/careerJourney";

export type StudentJourneyState = {
    courses: StudentCourse[];
    openRuns: OpenCourseRun[];
    interviews: InterviewActivity[];
    feedback: InterviewFeedback[];
    journey: ReturnType<typeof buildCareerJourney>;
    currentCourses: StudentCourse[];
    upcomingCourses: StudentCourse[];
    completedCourses: StudentCourse[];
    recommendation?: OpenCourseRun;
    loading: boolean;
    error: string;
    refresh: () => Promise<void>;
};

export default function useStudentJourney(uid: string | undefined, mockInterviewsEnabled: boolean): StudentJourneyState {
    const [courses, setCourses] = useState<StudentCourse[]>([]);
    const [openRuns, setOpenRuns] = useState<OpenCourseRun[]>([]);
    const [interviews, setInterviews] = useState<InterviewActivity[]>([]);
    const [feedback, setFeedback] = useState<InterviewFeedback[]>([]);
    const [loading, setLoading] = useState(Boolean(uid));
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        if (!uid) return;
        setError("");
        const [courseResult, opportunityResult] = await Promise.all([
            getStudentCourses().then((value) => ({ value })).catch((cause) => ({ cause })),
            listOpenCourseRuns().then((value) => ({ value })).catch((cause) => ({ cause })),
        ]);
        if ("value" in courseResult) setCourses(courseResult.value);
        if ("value" in opportunityResult) setOpenRuns(opportunityResult.value);
        if ("cause" in courseResult) setError(courseResult.cause instanceof Error ? courseResult.cause.message : "Your enrolled programs could not be loaded.");
        else if ("cause" in opportunityResult) setError("Upcoming opportunities could not be loaded. Your program history is still available.");
        setLoading(false);
    }, [uid]);

    useEffect(() => {
        if (!uid) {
            setCourses([]); setOpenRuns([]); setInterviews([]); setFeedback([]); setLoading(false); setError("");
            return;
        }
        setLoading(true);
        void refresh();
        const timer = window.setInterval(() => void refresh(), 60_000);
        return () => window.clearInterval(timer);
    }, [uid, refresh]);

    useEffect(() => {
        if (!uid || !mockInterviewsEnabled) {
            setInterviews([]); setFeedback([]);
            return;
        }
        const stopRequests = onSnapshot(query(collection(db, "interviewRequests"), where("studentId", "==", uid)), (snapshot) => {
            setInterviews(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as InterviewActivity)));
        }, () => setInterviews([]));
        const stopFeedback = onSnapshot(query(collection(db, "feedback"), where("studentId", "==", uid)), (snapshot) => {
            setFeedback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as InterviewFeedback)));
        }, () => setFeedback([]));
        return () => { stopRequests(); stopFeedback(); };
    }, [uid, mockInterviewsEnabled]);

    const journey = useMemo(() => buildCareerJourney(courses, interviews, feedback, mockInterviewsEnabled), [courses, interviews, feedback, mockInterviewsEnabled]);
    const currentCourses = useMemo(() => currentStudentCourses(courses), [courses]);
    const upcomingCourses = useMemo(() => upcomingStudentCourses(courses), [courses]);
    const completedCourses = useMemo(() => completedStudentCourses(courses), [courses]);
    const recommendation = useMemo(() => selectRecommendedProgram(openRuns, courses), [openRuns, courses]);
    return { courses, openRuns, interviews, feedback, journey, currentCourses, upcomingCourses, completedCourses, recommendation, loading, error, refresh };
}
